import { useState, useEffect } from 'react'
import { get, post, put, del } from '../utils/api'
import { IconPlus, IconEdit, IconTrash, IconCopy, IconCheck, IconMail, IconRobot, IconForms, IconSettings } from '@tabler/icons-react'

const TABS = [
  { key: 'templates', label: 'Email Templates', icon: IconMail },
  { key: 'autotraces', label: 'Autotraces', icon: IconRobot },
  { key: 'forms', label: 'Web Lead Forms', icon: IconForms },
]

/* ─── EMAIL TEMPLATES TAB ─── */
function EmailTemplates() {
  const [templates, setTemplates] = useState([])
  const [editing, setEditing] = useState(null)
  const [form, setForm] = useState({ name: '', subject: '', body: '', category: 'general' })
  const load = () => get('/email-templates').then(setTemplates).catch(() => {})
  useEffect(() => { load() }, [])

  const CATS = ['general', 'proposal', 'contract', 'follow_up', 'beo', 'confirmation']
  const MERGE = ['{{contact_name}}', '{{company}}', '{{event_name}}', '{{event_date}}', '{{hotel_name}}']

  const save = async () => {
    try {
      if (editing) await put(`/email-templates/${editing}`, form)
      else await post('/email-templates', form)
      setEditing(null); setForm({ name: '', subject: '', body: '', category: 'general' }); load()
    } catch (err) { alert(err.message) }
  }
  const startEdit = (t) => { setEditing(t.id); setForm({ name: t.name, subject: t.subject, body: t.body, category: t.category }) }
  const remove = async (id) => { if (confirm('Delete this template?')) { await del(`/email-templates/${id}`); load() } }
  const cancel = () => { setEditing(null); setForm({ name: '', subject: '', body: '', category: 'general' }) }

  return (
    <div>
      {/* Form */}
      <div className="card mb-3">
        <div className="card-header"><h3 className="card-title mb-0">{editing ? 'Edit Template' : 'New Template'}</h3></div>
        <div className="card-body">
          <div className="row g-3">
            <div className="col-6"><label className="form-label">Name</label><input className="form-control" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="e.g. Proposal Follow-Up" /></div>
            <div className="col-6"><label className="form-label">Category</label>
              <select className="form-select" value={form.category} onChange={e => setForm({ ...form, category: e.target.value })}>
                {CATS.map(c => <option key={c} value={c}>{c.replace(/_/g, ' ')}</option>)}
              </select>
            </div>
            <div className="col-12"><label className="form-label">Subject</label><input className="form-control" value={form.subject} onChange={e => setForm({ ...form, subject: e.target.value })} placeholder="Subject line with {{merge_fields}}" /></div>
            <div className="col-12">
              <label className="form-label">Body</label>
              <textarea className="form-control" rows="5" value={form.body} onChange={e => setForm({ ...form, body: e.target.value })} placeholder="Email body..." />
              <div style={{ fontSize: '0.75rem', color: '#64748b', marginTop: 4 }}>Merge fields: {MERGE.map(m => <code key={m} className="me-1" style={{ cursor: 'pointer', background: '#f1f5f9', padding: '1px 4px', borderRadius: 3 }} onClick={() => setForm({ ...form, body: form.body + m })}>{m}</code>)}</div>
            </div>
          </div>
        </div>
        <div className="card-footer d-flex gap-2">
          <button className="btn btn-primary btn-sm" onClick={save} disabled={!form.name || !form.subject}>{editing ? 'Update' : 'Create'} Template</button>
          {editing && <button className="btn btn-ghost-secondary btn-sm" onClick={cancel}>Cancel</button>}
        </div>
      </div>

      {/* List */}
      <div className="card">
        <div className="table-responsive">
          <table className="table table-vcenter card-table">
            <thead><tr><th>Name</th><th>Subject</th><th>Category</th><th style={{ width: 100 }}></th></tr></thead>
            <tbody>
              {templates.map(t => (
                <tr key={t.id}>
                  <td className="fw-medium">{t.name}</td>
                  <td className="text-secondary" style={{ fontSize: '0.85rem' }}>{t.subject}</td>
                  <td><span className="badge bg-blue-lt">{t.category?.replace(/_/g, ' ')}</span></td>
                  <td>
                    <div className="d-flex gap-1">
                      <button className="btn btn-ghost-primary btn-sm btn-icon" onClick={() => startEdit(t)} title="Edit"><IconEdit size={14} /></button>
                      <button className="btn btn-ghost-danger btn-sm btn-icon" onClick={() => remove(t.id)} title="Delete"><IconTrash size={14} /></button>
                    </div>
                  </td>
                </tr>
              ))}
              {!templates.length && <tr><td colSpan="4" className="text-center text-secondary py-4">No email templates yet. Create one above.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}

/* ─── AUTOTRACES TAB ─── */
function Autotraces() {
  const [traces, setTraces] = useState([])
  const [editing, setEditing] = useState(null)
  const [form, setForm] = useState({ name: '', trigger_days: 14, trigger_from: 'arrival', trigger_direction: 'before', task_type: 'task', assign_to_role: 'sales_manager', email_notify: false })
  const load = () => get('/autotraces/templates').then(setTraces).catch(() => {})
  useEffect(() => { load() }, [])

  const save = async () => {
    try {
      if (editing) await put(`/autotraces/templates/${editing}`, form)
      else await post('/autotraces/templates', form)
      setEditing(null); setForm({ name: '', trigger_days: 14, trigger_from: 'arrival', trigger_direction: 'before', task_type: 'task', assign_to_role: 'sales_manager', email_notify: false }); load()
    } catch (err) { alert(err.message) }
  }
  const startEdit = (t) => { setEditing(t.id); setForm({ name: t.name, trigger_days: t.trigger_days, trigger_from: t.trigger_from, trigger_direction: t.trigger_direction, task_type: t.task_type, assign_to_role: t.assign_to_role || '', email_notify: t.email_notify }) }
  const toggle = async (t) => { await put(`/autotraces/templates/${t.id}`, { is_active: !t.is_active }); load() }
  const remove = async (id) => { if (confirm('Delete this autotrace?')) { await del(`/autotraces/templates/${id}`); load() } }
  const cancel = () => { setEditing(null); setForm({ name: '', trigger_days: 14, trigger_from: 'arrival', trigger_direction: 'before', task_type: 'task', assign_to_role: 'sales_manager', email_notify: false }) }

  return (
    <div>
      <div className="alert alert-info mb-3" style={{ fontSize: '0.85rem' }}>Autotraces automatically create tasks when events are booked. Go to any event and click "Generate Autotraces" to apply these templates.</div>

      <div className="card mb-3">
        <div className="card-header"><h3 className="card-title mb-0">{editing ? 'Edit Autotrace' : 'New Autotrace'}</h3></div>
        <div className="card-body">
          <div className="row g-3">
            <div className="col-12"><label className="form-label">Task Name</label><input className="form-control" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="e.g. Send BEO to Client" /></div>
            <div className="col-3"><label className="form-label">Days</label><input type="number" className="form-control" value={form.trigger_days} onChange={e => setForm({ ...form, trigger_days: parseInt(e.target.value) || 0 })} min="0" /></div>
            <div className="col-3"><label className="form-label">Direction</label>
              <select className="form-select" value={form.trigger_direction} onChange={e => setForm({ ...form, trigger_direction: e.target.value })}>
                <option value="before">Before</option><option value="after">After</option>
              </select>
            </div>
            <div className="col-3"><label className="form-label">Relative To</label>
              <select className="form-select" value={form.trigger_from} onChange={e => setForm({ ...form, trigger_from: e.target.value })}>
                <option value="arrival">Arrival</option><option value="departure">Departure</option><option value="created">Booking Date</option>
              </select>
            </div>
            <div className="col-3"><label className="form-label">Task Type</label>
              <select className="form-select" value={form.task_type} onChange={e => setForm({ ...form, task_type: e.target.value })}>
                <option value="task">Task</option><option value="email">Email</option><option value="call">Call</option><option value="follow_up">Follow Up</option>
              </select>
            </div>
            <div className="col-6"><label className="form-label">Assign To Role</label>
              <select className="form-select" value={form.assign_to_role} onChange={e => setForm({ ...form, assign_to_role: e.target.value })}>
                <option value="sales_manager">Sales Manager</option><option value="sales_coordinator">Sales Coordinator</option><option value="dos">Director of Sales</option><option value="admin">GM / Admin</option>
              </select>
            </div>
            <div className="col-6 d-flex align-items-end">
              <label className="form-check"><input type="checkbox" className="form-check-input" checked={form.email_notify} onChange={e => setForm({ ...form, email_notify: e.target.checked })} /><span className="form-check-label">Send email notification</span></label>
            </div>
          </div>
        </div>
        <div className="card-footer d-flex gap-2">
          <button className="btn btn-primary btn-sm" onClick={save} disabled={!form.name}>{editing ? 'Update' : 'Create'} Autotrace</button>
          {editing && <button className="btn btn-ghost-secondary btn-sm" onClick={cancel}>Cancel</button>}
        </div>
      </div>

      <div className="card">
        <div className="table-responsive">
          <table className="table table-vcenter card-table">
            <thead><tr><th>Name</th><th>Trigger</th><th>Type</th><th>Assign To</th><th>Active</th><th style={{ width: 80 }}></th></tr></thead>
            <tbody>
              {traces.map(t => (
                <tr key={t.id} style={{ opacity: t.is_active ? 1 : 0.5 }}>
                  <td className="fw-medium">{t.name}</td>
                  <td style={{ fontSize: '0.85rem' }}>{t.trigger_days} days {t.trigger_direction} {t.trigger_from}</td>
                  <td><span className="badge bg-blue-lt">{t.task_type}</span></td>
                  <td style={{ fontSize: '0.85rem', textTransform: 'capitalize' }}>{t.assign_to_role?.replace(/_/g, ' ')}</td>
                  <td><button className={`btn btn-sm ${t.is_active ? 'btn-success' : 'btn-secondary'}`} onClick={() => toggle(t)} style={{ padding: '2px 8px', fontSize: '0.75rem' }}>{t.is_active ? 'On' : 'Off'}</button></td>
                  <td>
                    <div className="d-flex gap-1">
                      <button className="btn btn-ghost-primary btn-sm btn-icon" onClick={() => startEdit(t)}><IconEdit size={14} /></button>
                      <button className="btn btn-ghost-danger btn-sm btn-icon" onClick={() => remove(t.id)}><IconTrash size={14} /></button>
                    </div>
                  </td>
                </tr>
              ))}
              {!traces.length && <tr><td colSpan="6" className="text-center text-secondary py-4">No autotraces configured. Create one above.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}

/* ─── WEB LEAD FORMS TAB ─── */
function WebForms() {
  const [forms, setForms] = useState([])
  const [embed, setEmbed] = useState(null)
  const [editing, setEditing] = useState(null)
  const [form, setForm] = useState({ name: 'Website Inquiry', notify_email: '', redirect_url: '' })
  const [copied, setCopied] = useState(false)
  const load = () => get('/web-forms').then(setForms).catch(() => {})
  useEffect(() => { load() }, [])

  const save = async () => {
    try {
      if (editing) await put(`/web-forms/${editing}`, form)
      else await post('/web-forms', form)
      setEditing(null); setForm({ name: 'Website Inquiry', notify_email: '', redirect_url: '' }); load()
    } catch (err) { alert(err.message) }
  }
  const getEmbed = async (id) => {
    const data = await get(`/web-forms/${id}/embed`)
    setEmbed(data)
  }
  const copyEmbed = () => {
    navigator.clipboard.writeText(embed.embedCode)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }
  const toggle = async (f) => { await put(`/web-forms/${f.id}`, { is_active: !f.is_active }); load() }
  const remove = async (id) => { if (confirm('Delete this form?')) { await del(`/web-forms/${id}`); load() } }

  return (
    <div>
      <div className="alert alert-info mb-3" style={{ fontSize: '0.85rem' }}>Create embeddable lead forms for your hotel website. Submissions automatically create new leads and contacts in your CRM.</div>

      <div className="card mb-3">
        <div className="card-header"><h3 className="card-title mb-0">{editing ? 'Edit Form' : 'New Form'}</h3></div>
        <div className="card-body">
          <div className="row g-3">
            <div className="col-12"><label className="form-label">Form Name</label><input className="form-control" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Website Inquiry" /></div>
            <div className="col-6"><label className="form-label">Notification Email</label><input className="form-control" value={form.notify_email} onChange={e => setForm({ ...form, notify_email: e.target.value })} placeholder="sales@yourhotel.com" /></div>
            <div className="col-6"><label className="form-label">Redirect URL (optional)</label><input className="form-control" value={form.redirect_url} onChange={e => setForm({ ...form, redirect_url: e.target.value })} placeholder="https://yourhotel.com/thank-you" /></div>
          </div>
        </div>
        <div className="card-footer">
          <button className="btn btn-primary btn-sm" onClick={save} disabled={!form.name}>{editing ? 'Update' : 'Create'} Form</button>
        </div>
      </div>

      {/* Embed Modal */}
      {embed && (
        <div className="card mb-3 border-primary">
          <div className="card-header d-flex justify-content-between">
            <h3 className="card-title mb-0">Embed Code</h3>
            <button className="btn-close" onClick={() => setEmbed(null)} />
          </div>
          <div className="card-body">
            <p style={{ fontSize: '0.85rem' }} className="text-secondary mb-2">Copy this code and paste it into your hotel website HTML:</p>
            <div style={{ background: '#1e293b', borderRadius: 6, padding: 12, fontFamily: 'monospace', fontSize: '0.8rem', color: '#e2e8f0', wordBreak: 'break-all' }}>{embed.embedCode}</div>
            <div className="d-flex gap-2 mt-3">
              <button className="btn btn-primary btn-sm d-flex align-items-center gap-1" onClick={copyEmbed}>
                {copied ? <><IconCheck size={14} /> Copied!</> : <><IconCopy size={14} /> Copy Code</>}
              </button>
              <a href={embed.formUrl} target="_blank" rel="noopener noreferrer" className="btn btn-ghost-primary btn-sm">Preview Form</a>
            </div>
          </div>
        </div>
      )}

      {/* List */}
      <div className="card">
        <div className="table-responsive">
          <table className="table table-vcenter card-table">
            <thead><tr><th>Form Name</th><th>Notify</th><th>Status</th><th style={{ width: 160 }}></th></tr></thead>
            <tbody>
              {forms.map(f => (
                <tr key={f.id}>
                  <td className="fw-medium">{f.name}</td>
                  <td className="text-secondary" style={{ fontSize: '0.85rem' }}>{f.notify_email || '-'}</td>
                  <td><span className={`badge ${f.is_active ? 'bg-green-lt' : 'bg-secondary-lt'}`}>{f.is_active ? 'Active' : 'Inactive'}</span></td>
                  <td>
                    <div className="d-flex gap-1">
                      <button className="btn btn-ghost-primary btn-sm" onClick={() => getEmbed(f.id)} title="Get Embed Code"><IconCopy size={14} /></button>
                      <button className={`btn btn-sm ${f.is_active ? 'btn-ghost-warning' : 'btn-ghost-success'}`} onClick={() => toggle(f)}>{f.is_active ? 'Disable' : 'Enable'}</button>
                      <button className="btn btn-ghost-danger btn-sm btn-icon" onClick={() => remove(f.id)}><IconTrash size={14} /></button>
                    </div>
                  </td>
                </tr>
              ))}
              {!forms.length && <tr><td colSpan="4" className="text-center text-secondary py-4">No web forms yet. Create one above.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}

/* ─── MAIN SETTINGS PAGE ─── */
export default function SettingsPage() {
  const [tab, setTab] = useState('templates')

  return (
    <div>
      <div className="d-flex align-items-center gap-2 mb-3">
        <IconSettings size={24} />
        <h2 className="mb-0">Settings</h2>
      </div>

      <ul className="nav nav-tabs mb-3">
        {TABS.map(t => (
          <li key={t.key} className="nav-item">
            <button className={`nav-link d-flex align-items-center gap-1 ${tab === t.key ? 'active' : ''}`} onClick={() => setTab(t.key)}>
              <t.icon size={16} /> {t.label}
            </button>
          </li>
        ))}
      </ul>

      {tab === 'templates' && <EmailTemplates />}
      {tab === 'autotraces' && <Autotraces />}
      {tab === 'forms' && <WebForms />}
    </div>
  )
}
